import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { iebcApi, IEBCVoter } from '@services/api/iebc';

/** Shortest input that asks the server for suggestions (matches the API). */
export const MIN_SEARCH = 2;

/** Wraps each typed word inside `text` in <strong>, case-insensitively. */
const Highlight: React.FC<{ text: string; words: string[] }> = ({ text, words }) => {
  const ws = words.filter((w) => w.length > 0).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (ws.length === 0) return <>{text}</>;
  const parts = text.split(new RegExp(`(${ws.join('|')})`, 'gi'));
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <Box key={i} component="strong" sx={{ color: 'primary.main', fontWeight: 700 }}>
            {p}
          </Box>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        ),
      )}
    </>
  );
};

/**
 * Search box with suggestions: as the user types (2+ characters), the best
 * few matching people in their location appear under it — by name, ID
 * number, father's name, clan name, sublocation or village. Picking one
 * calls onPick; Enter (or the Search button beside it) calls onSearch with
 * everything typed.
 */
export const SearchBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onPick: (v: IEBCVoter) => void;
  onSearch: (text: string) => void;
  error?: string;
}> = ({ value, onChange, onPick, onSearch, error }) => {
  // Ask the server only once typing pauses.
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), 250);
    return () => clearTimeout(t);
  }, [value]);

  const enabled = debounced.replace(/\s/g, '').length >= MIN_SEARCH;
  const { data, isFetching } = useQuery({
    queryKey: ['iebc', 'suggest', debounced],
    queryFn: () => iebcApi.suggest(debounced),
    enabled,
    staleTime: 60 * 1000,
  });
  const options = enabled ? data ?? [] : [];
  const words = value.trim().split(/\s+/);

  return (
    <Autocomplete<IEBCVoter, false, false, true>
      freeSolo
      fullWidth
      size="small"
      options={options}
      filterOptions={(x) => x}
      inputValue={value}
      onInputChange={(_, v, reason) => {
        if (reason !== 'reset') onChange(v);
      }}
      onChange={(_, v, reason) => {
        if (reason === 'selectOption' && v && typeof v !== 'string') onPick(v);
        // Enter on typed text (no suggestion highlighted).
        else if (reason === 'createOption' && typeof v === 'string') onSearch(v);
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.full_name)}
      isOptionEqualToValue={(a, b) => a.national_id === b.national_id}
      loading={isFetching}
      loadingText="Searching…"
      noOptionsText={enabled ? 'No one in your location matches' : 'Keep typing…'}
      renderOption={(props, o) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key ?? o.national_id} {...rest}>
            <Box sx={{ minWidth: 0, py: 0.25 }}>
              <Typography sx={{ fontSize: '0.92rem' }} noWrap>
                <Highlight text={o.full_name} words={words} />
                <Box component="span" sx={{ ml: 1, fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.85rem' }}>
                  <Highlight text={o.national_id} words={words} />
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }} noWrap>
                Father: <Highlight text={o.father_name || '—'} words={words} />
                {' · '}Clan: <Highlight text={o.family_name || '—'} words={words} />
                {' · '}
                <Highlight text={[o.sub_location, o.village].filter(Boolean).join(' › ') || '—'} words={words} />
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search name, ID number, father's name, clan name or village"
          error={!!error}
          helperText={error || undefined}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBox;
